/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Button,
  Heading,
  Search,
  SvgIcon,
  Select,
  Spinner,
  DeductionPayments,
  CenteredContainerDisplay,
  DeductionUploadConfirm,
  Modal,
  SpanWithData,
  Warning,
  DeductionHeader,
  Summary
} from '../components'

import {
  decodeBase64,
  hasResponsibility,
  hasAnyResponsibility
} from '../utils'

class Deduction extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string
  }

  constructor(props) {
    super(props)

    this.state = {
      filter: '',
      searchString: '',
      prevSearchString: '',
      search: false,
      query: false,
      refetch: false,
      uploadConfirm: false,
      fileUploaded: null,
      userHasUpdatePermission: null,
      userIsPicsaEmployee: JSON.parse(localStorage.getItem("isUserPicsaEmployee")),
      showDownloadSuccessModal: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }
  }

  showUploadConfirmModal = () => (
    <DeductionUploadConfirm
      file={this.state.fileUploaded}
      changes={this.state.uploadChanges}
      prevented={this.state.uploadPrevented}
      errors={this.state.uploadErrors}
      ambiguous={this.state.uploadAmbiguous}
      onCancel={() => this.setState({
        uploadConfirm: false,
        fileUploaded: null
      })}
      onSaved={() => this.setState(
        {
          uploadConfirm: false,
          fileUploaded: null
        }, () => this.props.data.refetch()
      )}
    />
  )

  showUploadError = () => (
    <Modal
      body={
        <div>
          <Warning className="slds-align_absolute-center slds-m-bottom_medium" iconSize="medium">
            <Heading className="slds-align_absolute-center">
              File upload failed.
            </Heading>
          </Warning>
        </div>
      }
      footer={
        <Button
          onClick={() => this.setState({ uploadError: false })}
          className="slds-align_absolute-center">
          OK
        </Button>
      }
    />

  )

  showProcessingUpload = () => (
    <Modal
      body={
        <CenteredContainerDisplay
          height={300}
          yAdjustPercentage={-5}
          content={
            <Spinner type="track">
              Processing
            </Spinner>
          }
        />
      }
    />
  )

  downloadSpreadsheetFile = () => {
    let body = new FormData()
    body.append('workflowrun', decodeBase64(this.props.match.params.deduction))

    fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrundeductionsdownload/`, {
      method: 'post',
      headers: {
        "Authorization": "Token " + localStorage.getItem('token') || null
      },
      body: body
    })
    .then(data => {
      if (data.ok) {  // covers 200-range of http responses
        console.log("Download request successful.")
        this.setState({ showDownloadSuccessModal: true })
      } else {
        console.log(`Download request failed.  HTTP: ${data.status} - ${data.statusText}`)
      }
      return data.json()
    })
    .catch(error => {
      console.log('Download request failed', error)
    })
  }

  uploadSpreadsheetFile = (e) => {
    let file = e.target.files[0]
    let body = new FormData()
    body.append('workflowrun', this.props.match.params.deduction)
    body.append('file', file)

    this.setState({ processingUpload: true })
    fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrundeductionsupload/`, {
      method: 'post',
      headers: {
        "Authorization": "Token " + localStorage.getItem('token') || null
      },
      body: body
    })
    .then((response) => {
      if (response.ok) {  // covers 200-range of http responses
        console.log("Upload request successful.")
        response.json()
        .then((data) => {
          this.setState({
            fileUploaded: file.name,
            uploadChanges: data.changes,
            uploadPrevented: data.prevented,
            uploadErrors: data.errors,
            uploadAmbiguous: data.ambiguous,
            uploadConfirm: true,
            processingUpload: false
          })
        }, () => console.log("Something went wrong..."))
      } else {
        console.log(`Upload request failed.  HTTP: ${response.status} - ${response.statusText}`)
        this.setState({
          uploadError: true,
          processingUpload: false
        })
      }
    })
    .catch((error) => {
      console.log('Upload request failed', error)
      this.setState({
        uploadError: true,
        processingUpload: false
      })
    })
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner type='brand' size='large' top={true} className='viewport-height-100' />
    }

    if (this.props.data.error) {
      return (
        <Warning className='slds-m-around_large u-font-size-larger'>
          We&rsquo;re experiencing a problem loading your data
        </Warning>
      )
    }

    let deduction = this.props.data.workflowrun
    if (deduction.paymentsDeducted.edges.length === 0) {
      return (
        <div className='slds-m-around_large u-font-size-larger'>
          <SvgIcon
            variant='utility'
            symbol='info'
            size='small'
            colour='light'
            className='slds-m-right_medium'
          />
          No payments found. Deduction status: <SpanWithData
            forField='WorkflowRunStatus'
            searchKey={deduction.status} />
        </div>
      )
    }

    let payrollRun = deduction.payrollrunDeducted
    let payrollCycle = payrollRun.payrollcycle.id

    if (this.props.data.workflowrun && this.state.userHasUpdatePermission === null) {
      if (this.state.userIsPicsaEmployee) {
        this.setState({
          userHasUpdatePermission: hasResponsibility("self", "deductions_editor", "payrollcycle")
        })
      } else {
        let relatedInstance = decodeBase64(payrollCycle)
        this.setState({
          userHasUpdatePermission: hasAnyResponsibility("self", [
            {
              description: "deductions_editor",
              relatedModel: "payrollcycle",
              relatedInstance
            },
            {
              description: "deductions_approver_amounts",
              relatedModel: "payrollcycle",
              relatedInstance
            },
            {
              description: "deductions_approver_payment",
              relatedModel: "payrollcycle",
              relatedInstance
            }
          ])
        })
      }
    }

    if (hasResponsibility("self", "deductions_reviewer", 'payrollcycle', payrollCycle) ||
      this.state.userHasUpdatePermission ||
      this.state.userIsPicsaEmployee) {
      // TODO: handle no payments on filter change or new search
      let parentPayment = payrollRun.batchPayment
      let uploadButtonDisabled = !(this.state.userIsPicsaEmployee || this.state.userHasUpdatePermission) ||
        deduction.atStep > 8 ||
        !!deduction.workflowrunsteps.edges.find(({ node: workflowRunStep }) => (
          workflowRunStep.workflowstep.number === 8 &&
          workflowRunStep.status === "PROCESSED"
        ))

      return (
        <div className="slds-size_1-of-1">
          <DeductionHeader
            deduction={deduction}
            payrollDate={payrollRun.endsOn}
            payrollCycleId={payrollRun.payrollcycle.id}
            refresh={() => this.props.data.refetch()}
          />
          {this.state.uploadConfirm && this.showUploadConfirmModal()}
          {this.state.uploadError && this.showUploadError()}
          {this.state.processingUpload && this.showProcessingUpload()}
          <div className="slds-grid slds-wrap slds-size_1-of-1" style={{ height: "100%" }}>
            <div className="slds-col slds-size_8-of-12 slds-float_left">
              <div className="slds-grid slds-wrap slds-p-around_x-large u-bg-color-white slds-border_top">
                <div className="slds-p-top_xxx-small slds-p-right_medium">
                  Show:
                </div>
                <Select
                  value={this.state.filter}
                  options={[
                    {
                      value: '',
                      label: "All payments"
                    },
                    {
                      value: "new",
                      label: "New payments"
                    },
                    {
                      value: "changed",
                      label: "Changed payments"
                    },
                    {
                      value: "unchanged",
                      label: "Unchanged payments"
                    },
                    {
                      value: "removed",
                      label: "Removed payments"
                    }
                  ]}
                  onChange={e => this.setState({ filter: e.target.value })}
                />
                <div className="slds-p-left_large">
                  <Search
                    placeholder="Search"
                    value={this.state.searchString}
                    onChange={(e) => {
                      let searchString = e.target.value
                      this.setState((prevState) => {
                        if (prevState.search) {
                          return {
                            searchString,
                            search: false
                          }
                        } else {
                          return { searchString }
                        }
                      })
                    }}
                    onClick={() => this.setState({
                      search: true,
                      prevSearchString: this.state.searchString
                    })}
                    onClear={() => this.setState({
                      searchString: '',
                      prevSearchString: '',
                      search: false
                    })}
                  />
                </div>
                <div className="slds-col_bump-left slds-grid">
                  <Button
                    type="reset"
                    disabled={uploadButtonDisabled}
                    className="slds-m-right_large">
                    <div>
                      {/* programmatically trigger file browser using label & input */}
                      <label
                        className={`u-text-caps${uploadButtonDisabled ? " slds-text-color_weak" : " slds-button"}`}
                        htmlFor="uploadFileInput"
                        style={{ cursor: "pointer" }}>
                        <SvgIcon
                          variant="picsaos"
                          symbol="upload"
                        />
                        <br />
                        Upload
                      </label>
                      <input
                        id="uploadFileInput"
                        type="file"
                        style={{ display: "none" }}
                        disabled={uploadButtonDisabled}
                        onClick={(e) => e.target.value = null}  // this line was necessary to allow the same file to be uploaded back-to-back
                        onChange={this.uploadSpreadsheetFile}
                      />
                    </div>
                  </Button>
                  <Button
                    type="reset"
                    onClick={this.downloadSpreadsheetFile}>
                    <SvgIcon
                      variant="picsaos"
                      symbol="download"
                    />
                    <div className="u-text-caps">Download</div>
                  </Button>
                </div>
              </div>
              <div className="slds-border_top">
                <DeductionPayments
                  deductionId={this.props.match.params.deduction}
                  payrollCycleId={parentPayment.payrollruns.edges[0].node.payrollcycle.id}
                  deductionParameters={payrollRun.paymentsBreakdown}
                  filter={this.state.filter}
                  search={this.state.search  // could remove the state.search check if we want instant search results as you type
                    ? this.state.searchString
                    : this.state.prevSearchString
                  }
                  onRemoveSearchFilterClick={() => this.setState({
                    search: false,
                    searchString: ''
                  })}
                  readOnly={!this.state.userHasUpdatePermission ||
                    deduction.atStep > 8 ||
                    deduction.status === "PROCESSED"
                  }
                />
              </div>
            </div>
            <div className="slds-col slds-size_4-of-12 slds-text-color_inverse side-panel is-deduction-summary slds-p-around_xx-large">
              <Summary
                data={JSON.parse(payrollRun.paymentSummary)['payments']}
                grandTotal={payrollRun.batchPayment.parentTotalAmount}
              />
            </div>
          </div>
          {this.state.showDownloadSuccessModal &&
            <Modal
              body={
                <div
                  className="slds-size_2-of-3 slds-align_absolute-center"
                  style={{ display: "block" }}>
                  <div className="slds-m-bottom_large slds-align_absolute-center circleIcon">
                    <SvgIcon
                      variant="utility"
                      symbol="check"
                      size="large"
                      colour="success"
                    />
                  </div>
                  <div>
                    <Heading className="slds-text-align_center">
                      {`The spreadsheet has been emailed to ${localStorage.getItem("userEmailAddress")}.  Should you not receive it within 10 minutes, please contact us`}
                    </Heading>
                  </div>
                </div>
              }
              footer={
                <Button
                  type="brand"
                  onClick={() => this.setState({ showDownloadSuccessModal: false})}
                  className="slds-align_absolute-center">
                  OK
                </Button>
              }
            />
          }
        </div>
      )
    } else {
      return (
        <div className="slds-p-around_xx-large slds-grid">
          <SvgIcon
            variant="utility"
            symbol="lock"
            // colour=""
            className="slds-m-right_medium"
          />
          <Heading className="slds-m-top_x-small">
            Unfortunately, you don&rsquo;t have permission to view deduction details.
          </Heading>
        </div>
      )
    }
  }
}

// TODO: uncomment when using cases
// const OpenCaseNotification = (props) => (
//   <div>
//     <SvgIcon
//       variant="utility"
//       symbol="warning"
//       size="small"
//     />
//     <span className="slds-p-left_small" style={{ color: "mediumseagreen" }}>
//       <b>There's an open case for this deduction.</b>
//     </span>
//   </div>
// )

const getDeduction = gql`
  query ($id: ID!) {
    workflowrun(id: $id) {
      id
      atStep
      status
      paymentsDeducted {
        edges {
          node {
            id
          }
        }
      }
      workflowrunsteps {
        edges {
          node {
            id
            status
            workflowstep {
              id
              number
            }
          }
        }
      }
      payrollrunDeducted {
        id
        endsOn
        paymentsBreakdown
        status
        paymentSummary
        batchPayment {
          id
          parentTotalAmount
          payrollruns {
            edges {
              node {
                id
                payrollcycle {
                  id
                }
              }
            }
          }
        }
        payrollcycle {
          id
        }
      }
    }
  }
`

const withData =
  graphql(getDeduction, {
    options: (ownProps) => ({
      variables: {
        id: ownProps.match.params.deduction
      }
    })
  })

export default withData(Deduction)



// WEBPACK FOOTER //
// ./src/containers/Deduction.js